import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"
import { logError } from "@/lib/sentry";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default:
          "bg-primary hover:bg-primary/90 text-primary-foreground rounded-md px-4 py-2",
        primary:
          "cursor-pointer rounded-xl border border-white/[0.09] bg-white/[0.11] text-white shadow-[inset_0_1px_0_rgba(255,255,255,0.08),0_10px_26px_rgba(0,0,0,0.22)] transition-all duration-200 hover:-translate-y-px hover:border-white/[0.14] hover:bg-white/[0.16] hover:shadow-[inset_0_1px_0_rgba(255,255,255,0.12),0_14px_30px_rgba(0,0,0,0.28)] active:translate-y-0 active:scale-[0.99] focus-visible:ring-2 focus-visible:ring-steel_blue-400/35",
        destructive:
          "bg-red-600 text-white hover:bg-red-500 shadow-none border-0",
        outline: "bg-transparent border border-border text-foreground hover:bg-accent/10",
        secondary:
          "bg-secondary text-secondary-foreground hover:bg-secondary/80",
        ghost: cn(
          "rounded-lg border-none text-foreground",
          "bg-transparent hover:bg-accent/10 focus-visible:bg-accent/20",
          "focus-visible:ring-1 focus-visible:ring-white/10",
          "disabled:cursor-not-allowed disabled:opacity-50",
        ),
        link: "text-primary underline-offset-4 hover:underline",
      },
      size: {
        default: "h-12 sm:h-10 px-5 py-3 sm:px-4 sm:py-2",
        xs: "h-8 px-3 py-1.5",
        sm: "h-10 rounded-md px-4 py-2",
        lg: "h-14 rounded-md px-8 py-4",
        icon: "h-12 w-12 sm:h-10 sm:w-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
  VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    { className, variant, size, asChild = false, onClick, disabled, ...props },
    ref
  ) => {
    const Comp = asChild ? Slot : "button";

    const handleClick = React.useCallback(
      (e: React.MouseEvent<HTMLButtonElement>) => {
        try {
          // Prevent double clicks when disabled
          if (disabled) {
            e.preventDefault();
            return;
          }

          // Call the original onClick handler
          onClick?.(e);
        } catch (error) {
          logError("Button click error:", error);
        }
      },
      [onClick, disabled]
    );

    return (
      <Comp
        className={cn(buttonVariants({ variant, size }), className)}
        ref={ref}
        onClick={handleClick}
        disabled={disabled}
        {...props}
      />
    );
  }
);
Button.displayName = "Button"

export { Button, buttonVariants }
